/**
 * かな・異体字を同一視した検索（実装メモ T-25：10/01 ユーザー決定で現行 PCRM に合わせた）。
 * 現行 PCRM の backend/src/shared/variant-chars.ts の移植。異体字の表と比べ方（小文字 → 異体字 → 全角英数字を半角 →
 * カタカナをひらがな → 部分一致）は現行のまま変えない。表の重複（'神' 等）も現行のまま残す。
 * 例：「高橋」で検索 → 「髙橋」もヒット。「やまだ」で検索 → フリガナ「ヤマダ」もヒット
 */

/** 異体字グループ（各配列の最初の文字を正規形とし、残りを同一視する） */
const VARIANT_CHAR_GROUPS: readonly (readonly string[])[] = [
  // 人名・地名で頻出する異体字
  ['高', '髙', '㐭'], // 高橋、高田など
  ['崎', '﨑', '嵜', '碕'], // 山崎、宮崎など
  ['辺', '邊', '邉', '部'], // 渡辺、田辺など
  ['斉', '齊', '斎', '齋'], // 斉藤など
  ['沢', '澤'], // 金沢、沢田など
  ['浜', '濱'], // 横浜、浜田など
  ['広', '廣'], // 広島、広田など
  ['国', '國'], // 国分など
  ['島', '嶋', '嶌'], // 島田、中島など
  ['橋', '槁', '𣘸'], // 高橋、石橋など
  ['龍', '竜', '龗'], // 龍田、竜馬など
  ['徳', '德'], // 徳川、徳田など
  ['恵', '惠', '恵'], // 恵美子など
  ['栄', '榮'], // 栄一など
  ['学', '學'], // 学など
  ['亜', '亞'], // 亜希子など
  ['真', '眞'], // 真一など
  ['祐', '祐', '裕'], // 祐介など（注: 意味が異なる場合もある）
  ['祥', '祥'], // 祥子など
  ['瀬', '瀨'], // 広瀬など
  ['慶', '慶'], // 慶一など
  ['礼', '禮'], // 礼子など
  ['桜', '櫻'], // 桜井など
  ['鉄', '鐵', '鑞'], // 鉄男など
  ['寛', '寬'], // 寛太など
  ['宝', '寶'], // 宝田など
  ['実', '實'], // 実など
  ['晴', '晴'], // 晴美など
  ['関', '關'], // 関口など
  ['条', '條'], // 条など
  ['弥', '彌'], // 弥生など
  ['鋭', '銳'], // 鋭一など
  ['浅', '淺'], // 浅野など
  ['円', '圓'], // 円など
  ['芸', '藝'], // 芸能など
  ['郎', '郞'], // 太郎など
  ['尭', '堯'], // 尭など
  ['塩', '鹽'], // 塩田など
  ['与', '與'], // 与一など
  ['豊', '豐'], // 豊田など
  ['巌', '巖', '嵒'], // 巌など
  ['万', '萬'], // 万田など
  ['会', '會'], // 会田など
  ['伝', '傳'], // 伝など
  ['壱', '壹'], // 壱など
  ['富', '冨'], // 富田など
  ['峰', '峯'], // 峰など
  ['廃', '廢'], // 廃など
  ['応', '應'], // 応など
  ['戸', '戶'], // 戸田など
  ['斗', '鬥'], // 斗など
  ['曽', '曾'], // 曽根など
  ['栃', '櫪'], // 栃木など
  ['桧', '檜'], // 桧など
  ['梅', '梅'], // 梅田など
  ['渋', '澁'], // 渋谷など
  ['滝', '瀧'], // 滝など
  ['瑞', '瑞'], // 瑞穂など
  ['痩', '瘦'], // 痩など
  ['神', '神'], // 神田など（異体字として同一視）
  ['福', '福', '福'], // 福田など
  ['穂', '穗'], // 穂高など
  ['経', '經'], // 経など
  ['総', '總'], // 総一など
  ['縁', '緣'], // 縁など
  ['緑', '綠'], // 緑など
  ['県', '縣'], // 県など
  ['翠', '翆'], // 翠など
  ['虫', '蟲'], // 虫など
  ['視', '視'], // 視など
  ['謡', '謠'], // 謡など
  ['賓', '賓'], // 賓など
  ['転', '轉'], // 転など
  ['郷', '鄕', '郷'], // 郷など
  ['都', '都'], // 都など
  ['黒', '黑'], // 黒田など
  ['齢', '齡'], // 齢など

  // カタカナの異体字（主に長音記号）
  ['ー', '－', '―', 'ｰ'], // 長音記号の統一

  // 数字の異体字
  ['1', '１', '一', '壱', '壹'],
  ['2', '２', '二', '弐', '貳'],
  ['3', '３', '三', '参', '參'],
  ['4', '４', '四'],
  ['5', '５', '五'],
  ['6', '６', '六'],
  ['7', '７', '七'],
  ['8', '８', '八'],
  ['9', '９', '九'],
  ['0', '０', '零'],
];

/** 異体字 → 正規形（あとのグループが同じ文字を持つときは、あとのほうが勝つ：現行と同じ） */
const VARIANT_TO_NORMAL_FORM = new Map<string, string>();
for (const group of VARIANT_CHAR_GROUPS) {
  for (const variant of group) {
    VARIANT_TO_NORMAL_FORM.set(variant, group[0] ?? variant);
  }
}

/** 異体字を正規形にそろえる（例：'髙橋' → '高橋'、'渡邉' → '渡辺'）。空・値なしは空文字 */
export function normalizeVariantChars(text: string | null | undefined): string {
  if (!text) {
    return '';
  }
  let result = '';
  for (const char of text) {
    result += VARIANT_TO_NORMAL_FORM.get(char) ?? char;
  }
  return result;
}

/** カタカナをひらがなに（ァ〜ヶ） */
export function katakanaToHiragana(text: string | null | undefined): string {
  if (!text) {
    return '';
  }
  return text.replace(/[\u30A1-\u30F6]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0x60));
}

/** ひらがなをカタカナに（ぁ〜ゖ） */
export function hiraganaToKatakana(text: string | null | undefined): string {
  if (!text) {
    return '';
  }
  return text.replace(/[\u3041-\u3096]/g, (char) => String.fromCharCode(char.charCodeAt(0) + 0x60));
}

/** 全角の英数字を半角に。全角の空白は半角に、長音・ダッシュ（ー－―）は '-' に */
export function fullWidthToHalfWidth(text: string | null | undefined): string {
  if (!text) {
    return '';
  }
  return text
    .replace(/[Ａ-Ｚａ-ｚ０-９]/g, (char) => String.fromCharCode(char.charCodeAt(0) - 0xfee0))
    .replace(/\u3000/g, ' ')
    .replace(/[ー－―]/g, '-');
}

/** 検索用のそろえ：小文字 → 異体字 → 全角を半角 → カタカナをひらがな */
export function normalizeForSearch(text: string | null | undefined): string {
  if (!text) {
    return '';
  }
  return katakanaToHiragana(fullWidthToHalfWidth(normalizeVariantChars(text.toLowerCase())));
}

/** 異体字を考えた部分一致（大文字小文字も同一視）。どちらかが空なら false */
export function matchWithVariants(text: string | null | undefined, query: string | null | undefined): boolean {
  if (!text || !query) {
    return false;
  }
  return normalizeVariantChars(text.toLowerCase()).includes(normalizeVariantChars(query.toLowerCase()));
}

/** 名前またはフリガナのどこかに検索語が含まれるか（異体字・かな・全角半角を同一視）。検索語が空なら true */
export function matchWithVariantsAndKana(
  main_text: string | null | undefined,
  kana_text: string | null | undefined,
  query: string | null | undefined,
): boolean {
  if (!query) {
    return true;
  }
  const normalized_query = normalizeForSearch(query);
  return [main_text, kana_text].some((text) => Boolean(text) && normalizeForSearch(text).includes(normalized_query));
}
